import { useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

const DEFAULT_POD_VERSION = '96ee4393';
const SCRIPT_SUFFIX = '.pod-a5t.pages.dev/frame-builder.es.js';
const STORAGE_KEY = 'pod_script_version';
const URL_PARAM = 'pod';

const buildPodScriptUrl = (version: string) => `https://${version}${SCRIPT_SUFFIX}`;

const loadPodVersion = (): string => {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get(URL_PARAM);
    if (fromUrl) {
      localStorage.setItem(STORAGE_KEY, fromUrl);
      return fromUrl;
    }
    return localStorage.getItem(STORAGE_KEY) ?? DEFAULT_POD_VERSION;
  } catch {
    return DEFAULT_POD_VERSION;
  }
};

const savePodVersion = (version: string) => {
  try {
    if (version === DEFAULT_POD_VERSION) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, version);
    }
  } catch {
    // ignore
  }
};

const setUrlParam = (version: string | null) => {
  const params = new URLSearchParams(window.location.search);
  if (version) {
    params.set(URL_PARAM, version);
  } else {
    params.delete(URL_PARAM);
  }
  const search = params.toString();
  window.history.replaceState(
    null,
    '',
    search ? `${window.location.pathname}?${search}` : window.location.pathname
  );
};

export const getPodScriptConfig = () => {
  const version = loadPodVersion();
  return {
    url: buildPodScriptUrl(version),
    version,
    onError: () => {
      if (version !== DEFAULT_POD_VERSION) {
        savePodVersion(DEFAULT_POD_VERSION);
        setUrlParam(null);
        window.location.reload();
      }
    },
  };
};

// --- Dev bar ---

const DevBar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 28px;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(20, 20, 20, 0.88);
  font-family: monospace;
  font-size: 12px;
  color: #fff;
  padding: 0 10px;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`;

const ToolbarSection = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const VersionInput = styled.input`
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  color: #fff;
  font-family: monospace;
  font-size: 12px;
  padding: 2px 6px;
  width: 90px;
  outline: none;
  height: 20px;
  &:focus {
    border-color: rgba(255, 255, 255, 0.7);
  }
`;

const Btn = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  color: #fff;
  cursor: pointer;
  font-family: monospace;
  font-size: 11px;
  padding: 2px 7px;
  height: 20px;
  line-height: 1;
  &:hover {
    background: rgba(255, 255, 255, 0.28);
  }
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  font-family: monospace;
  font-size: 16px;
  line-height: 1;
  padding: 0 0 0 8px;
  &:hover {
    color: #fff;
  }
`;

const FloatingBadge = styled.span`
  position: fixed;
  top: 0;
  left: 10px;
  height: 28px;
  z-index: 100000;
  display: inline-flex;
  align-items: center;
  background: rgba(255, 160, 0, 0.9);
  color: #000;
  font-weight: 700;
  font-size: 11px;
  padding: 0 8px;
  border-radius: 4px;
  letter-spacing: 0.03em;
  pointer-events: none;
`;

export const PodDevBar = () => {
  const version = loadPodVersion();
  const showToolbar = new URLSearchParams(window.location.search).has(URL_PARAM);
  const isDevVersion = version !== DEFAULT_POD_VERSION;

  const [input, setInput] = useState(version);
  const [dismissed, setDismissed] = useState(false);

  if (!isDevVersion && !showToolbar) return null;

  const apply = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    savePodVersion(trimmed);
    setUrlParam(trimmed);
    window.location.reload();
  };

  const reset = () => {
    savePodVersion(DEFAULT_POD_VERSION);
    setUrlParam(null);
    window.location.reload();
  };

  return createPortal(
    <>
      {isDevVersion && <FloatingBadge>pod: {version}</FloatingBadge>}
      {!dismissed && (
        <DevBar>
          <div />
          {showToolbar && (
            <ToolbarSection>
              <span>version:</span>
              <VersionInput
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && apply()}
                spellCheck={false}
                placeholder={DEFAULT_POD_VERSION}
              />
              <Btn onClick={apply}>Apply</Btn>
              <Btn onClick={reset}>Reset</Btn>
            </ToolbarSection>
          )}
          <CloseBtn onClick={() => setDismissed(true)}>×</CloseBtn>
        </DevBar>
      )}
    </>,
    document.body
  );
};
